import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { getSortedSections, getArticlesForSection } from '../../config/documentation';
import './DocumentationViewer.css';

/**
 * Static Documentation Viewer Component
 * Displays markdown documentation files from public/docs folder
 */
function StaticDocumentationViewer({ selectedSection, onSelectArticle }) {
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleContent, setArticleContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredArticles, setFilteredArticles] = useState([]);

  const sections = getSortedSections();

  const activeSectionMeta = sections.find(s => s.id === selectedSection) || {};

  // Articles are provided by central config; viewer will query them per-section.

  // Load articles for selected section
  useEffect(() => {
    if (selectedSection) {
      const sectionArticles = getArticlesForSection(selectedSection) || [];
      setArticles(sectionArticles);
      setFilteredArticles(sectionArticles);
      setLoading(false);
      setSelectedArticle(null);
      setArticleContent('');
    }
  }, [selectedSection]);

  // Load article content
  const loadArticleContent = async (article) => {
    try {
      setLoading(true);
      const response = await fetch(`/docs/${article.file}?v=${Date.now()}`);
      if (!response.ok) {
        throw new Error('Failed to load article');
      }
      const content = await response.text();
      setArticleContent(content);
      setSelectedArticle(article);
      if (onSelectArticle) {
        onSelectArticle({ ...article, content });
      }
    } catch (error) {
      console.error('Failed to load article content:', error);
      setArticleContent('# Error\n\nFailed to load article content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const estimateReadTime = (article) => {
    const words = (article.description || '').split(/\s+/).filter(Boolean).length;
    const minutes = Math.max(2, Math.ceil(words / 250));
    return `${minutes} min read`;
  };

  const articleIconFor = (article) => {
    // Select icon by section or fallback
    if (selectedSection === 'getting-started') return '📘';
    if (selectedSection === 'scout-study-tools') return '🤖';
    if (selectedSection === 'account-billing') return '⚙️';
    if (selectedSection === 'help-troubleshooting') return '🔧';
    return '📄';
  };

  const ctaFor = (article) => {
    const title = (article.title || '').toLowerCase();
    if (title.includes('create') || title.includes('creating') || title.includes('setup') || title.includes('setup')) return 'Start setup →';
    return 'Open guide →';
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setFilteredArticles(articles);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = articles.filter(article => 
      article.title.toLowerCase().includes(query) ||
      article.description.toLowerCase().includes(query)
    );
    setFilteredArticles(filtered);
  };

  const handleArticleClick = (article) => {
    loadArticleContent(article);
  };

  const handleBackToList = () => {
    setSelectedArticle(null);
    setArticleContent('');
  };

  // If article is selected, show article view
  if (selectedArticle) {
    return (
      <div className="documentationViewer">
        <button className="docsBackButton" onClick={handleBackToList}>
          ← Back to {sections.find(s => s.id === selectedSection)?.name}
        </button>
        
        {loading ? (
          <div className="docsLoading">Loading article...</div>
        ) : (
          <div className="docsArticleContent">
            <ReactMarkdown
              components={{
                h1: ({node, children, ...props}) => <h1 className="docsMarkdownH1" {...props}>{children}</h1>,
                h2: ({node, children, ...props}) => <h2 className="docsMarkdownH2" {...props}>{children}</h2>,
                h3: ({node, children, ...props}) => <h3 className="docsMarkdownH3" {...props}>{children}</h3>,
                h4: ({node, children, ...props}) => <h4 className="docsMarkdownH4" {...props}>{children}</h4>,
                p: ({node, ...props}) => <p className="docsMarkdownP" {...props} />,
                ul: ({node, ...props}) => <ul className="docsMarkdownUl" {...props} />,
                ol: ({node, ...props}) => <ol className="docsMarkdownOl" {...props} />,
                li: ({node, ...props}) => <li className="docsMarkdownLi" {...props} />,
                code: ({node, inline, ...props}) => 
                  inline 
                    ? <code className="docsMarkdownInlineCode" {...props} />
                    : <code className="docsMarkdownCodeBlock" {...props} />,
                blockquote: ({node, ...props}) => <blockquote className="docsMarkdownBlockquote" {...props} />,
                table: ({node, ...props}) => <table className="docsMarkdownTable" {...props} />,
                a: ({node, children, ...props}) => <a className="docsMarkdownLink" target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
              }}
            >
              {articleContent}
            </ReactMarkdown>
            {/* Suggested next reads */}
            <div className="docsSuggestedReads">
              <h4>Suggested next reads</h4>
              <div className="docsSuggestedList">
                {getArticlesForSection(selectedSection)
                  .filter(a => a.id !== selectedArticle.id)
                  .slice(0, 3)
                  .map(a => (
                    <div key={a.id} className="docsSuggestedCard" onClick={() => loadArticleContent(a)}>
                      <span className="suggestIcon">{articleIconFor(a)}</span>
                      <div className="suggestInfo">
                        <div className="suggestTitle">{a.title}</div>
                        <div className="suggestMeta">{estimateReadTime(a)}</div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Otherwise, show article list
  return (
    <div className="documentationViewer">
      {/* Section header */}
      <div className="docsSectionHeader">
        <div className="docsSectionHeaderLeft">
          <h2>{activeSectionMeta.name}</h2>
          {activeSectionMeta.id === 'scout-study-tools' && (
            <div className="scoutMicrocopy">Powered by Scout</div>
          )}
        </div>
        {activeSectionMeta.description && (
          <p className="docsSectionPurpose">{activeSectionMeta.description}</p>
        )}
      </div>
      {/* Search Bar */}
      <div className="docsSearch">
        <form onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Search guides, features, and tools…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="docsSearchInput"
          />
          <button type="submit" className="docsSearchButton">
            🔍 Search
          </button>
        </form>
        <div className="docsSearchHint">Tip: Try “flashcards”, “calendar”, or “billing”</div>
      </div>

      {/* Articles List */}
      <div className="docsArticlesList">
        {loading ? (
          <div className="docsLoading">Loading...</div>
        ) : filteredArticles.length === 0 ? (
          <div className="docsEmpty">
            <p>📚 No documentation articles found.</p>
            {searchQuery && (
              <p className="docsEmptyHint">
                Try a different search term or clear your search.
              </p>
            )}
          </div>
        ) : (
          filteredArticles.map((article) => (
            <div
              key={article.id}
              className="docsArticleCard"
              onClick={() => handleArticleClick(article)}
            >
              <div className="articleCardLeft">
                <span className="articleIcon">{articleIconFor(article)}</span>
              </div>
              <div className="articleCardBody">
                <h3 className="docsArticleTitle">{article.title}</h3>
                {article.description && (
                  <p className="docsArticleDesc">{article.description}</p>
                )}
                <div className="docsArticleMeta">
                  <span className="docsReadTime">⏱ {estimateReadTime(article)}</span>
                  <span className="docsReadMore">{ctaFor(article)}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {searchQuery && (
        <button
          className="docsClearSearch"
          onClick={() => {
            setSearchQuery('');
            setFilteredArticles(articles);
          }}
        >
          Clear Search
        </button>
      )}
    </div>
  );
}

export default StaticDocumentationViewer;
